import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatGBP } from "../lib/api";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      <div className={`fixed inset-0 bg-black/40 z-50 transition-opacity ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setDrawerOpen(false)} data-testid="cart-overlay" />
      <aside className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-xl transform transition-transform duration-500 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-hair">
          <h3 className="font-serif text-2xl">Your Bag</h3>
          <button onClick={() => setDrawerOpen(false)} aria-label="Close" data-testid="cart-close">
            <X strokeWidth={1.25} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4" style={{maxHeight: "calc(100vh - 220px)"}}>
          {items.length === 0 ? (
            <div className="py-20 text-center" data-testid="cart-empty">
              <p className="font-serif text-xl mb-3">Your bag is empty</p>
              <p className="text-sm text-neutral-500 mb-6">Begin a quiet ritual of curation.</p>
              <Link to="/shop" onClick={() => setDrawerOpen(false)} className="btn-ghost">Browse Collection</Link>
            </div>
          ) : items.map((it) => (
            <div key={it.id} className="flex gap-4 py-5 border-b border-hair" data-testid={`cart-item-${it.id}`}>
              <Link to={`/product/${it.id}`} onClick={() => setDrawerOpen(false)} className="w-24 h-28 bg-sand flex-shrink-0">
                <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <p className="font-serif text-base">{it.name}</p>
                <p className="text-sm text-neutral-600">{formatGBP(it.price)}</p>
                <div className="flex items-center gap-2 mt-auto">
                  <button onClick={() => updateQty(it.id, it.quantity - 1)} data-testid={`qty-dec-${it.id}`} className="p-1 border border-hair"><Minus className="w-3 h-3" strokeWidth={1.25} /></button>
                  <span className="text-sm w-6 text-center" data-testid={`qty-${it.id}`}>{it.quantity}</span>
                  <button onClick={() => updateQty(it.id, it.quantity + 1)} data-testid={`qty-inc-${it.id}`} className="p-1 border border-hair"><Plus className="w-3 h-3" strokeWidth={1.25} /></button>
                  <button onClick={() => removeItem(it.id)} data-testid={`remove-${it.id}`} className="ml-auto text-neutral-500 hover:text-[var(--gold)]"><Trash2 strokeWidth={1.25} className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-hair px-6 py-5 bg-white">
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-neutral-600">Subtotal</span>
              <span data-testid="cart-subtotal">{formatGBP(subtotal)}</span>
            </div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-4">Shipping calculated at checkout</p>
            <button onClick={goCheckout} className="btn-primary w-full" data-testid="checkout-btn">Checkout</button>
          </div>
        )}
      </aside>
    </>
  );
}
